/**
 * @module Citation chip node for the Tiptap composer.
 *
 * Ported from t3code's composer rich-text layer (ticket 3526, upstream pin
 * e3e7cc3f, MIT license retained in LICENSE), carrying Forge's assistant
 * citation contract and comment editing.
 */

import { Node } from "@tiptap/core";
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type Editor,
  type NodeViewProps,
} from "@tiptap/react";
import { AssistantCitation } from "@cmmd-forge/contracts";
import { Option } from "effect";
import * as Schema from "effect/Schema";
import {
  serializeAssistantCitation,
  withAssistantCitationComment,
} from "@cmmd-forge/shared/assistantCitations";
import { createContext, useCallback, use, type ReactNode } from "react";
import type { AssistantCitationSourceAnchor } from "~/lib/assistantTextSelection";

import { AssistantCitationChip } from "./chat/AssistantCitationChip";

type ComposerCitationCommentTarget = {
  citeKey: string;
  sourceAnchor?: AssistantCitationSourceAnchor;
  removeOnCancel?: boolean;
};

const ComposerCitationCommentContext = createContext<{
  openComment: ComposerCitationCommentTarget | null;
  onOpenChange: (citeKey: string, open: boolean) => void;
  onSubmitAndSend: () => void;
}>({ openComment: null, onOpenChange: () => {}, onSubmitAndSend: () => {} });

type CitationEditorState = {
  readonly isEditable: () => boolean;
  readonly updateCitationComment: (citeKey: string, comment: string) => boolean;
  readonly removeCitation: (citeKey: string) => void;
};

const ComposerCitationEditorContext = createContext<CitationEditorState>({
  isEditable: () => false,
  updateCitationComment: () => false,
  removeCitation: () => {},
});

/** Seam the citation provider uses to insert a citation chip into the editor. */
type ComposerCitationInsert = (
  citation: AssistantCitation,
  sourceAnchor?: AssistantCitationSourceAnchor,
) => boolean;

export const ComposerCitationInsertContext = createContext<ComposerCitationInsert>(() => false);

/** Renders a citation chip with comment editing and removal guarded by editor editability. */
function ComposerCitationNodeView({ node }: NodeViewProps) {
  const commentContext = use(ComposerCitationCommentContext);
  const editorState = use(ComposerCitationEditorContext);
  const citation = node.attrs.citation as AssistantCitation | null;
  const citeKey = node.attrs.citeKey as string;
  const commentTarget =
    commentContext.openComment?.citeKey === citeKey ? commentContext.openComment : null;

  const onSaveComment = useCallback(
    (comment: string): boolean => {
      if (!editorState.isEditable()) return false;
      return editorState.updateCitationComment(citeKey, comment);
    },
    [citeKey, editorState],
  );

  const onRemove = useCallback(() => {
    if (!editorState.isEditable()) return;
    editorState.removeCitation(citeKey);
  }, [citeKey, editorState]);

  return (
    <NodeViewWrapper
      as="span"
      className="inline-flex min-w-0 max-w-full select-none"
      contentEditable={false}
      spellCheck={false}
      data-composer-citation-chip="true"
    >
      {citation ? (
        <AssistantCitationChip
          citation={citation}
          onRemove={onRemove}
          commentEditor={{
            open: commentTarget !== null,
            sourceAnchor: commentTarget?.sourceAnchor,
            onOpenChange: (open) => {
              if (open && !editorState.isEditable()) return;
              commentContext.onOpenChange(citeKey, open);
            },
            ...(commentTarget?.removeOnCancel ? { onCancel: onRemove } : {}),
            onSave: onSaveComment,
            onSaveAndSend: (comment) => {
              if (!onSaveComment(comment)) return false;
              commentContext.onSubmitAndSend();
              return true;
            },
          }}
        />
      ) : null}
    </NodeViewWrapper>
  );
}

// Compiled once at module scope; the forge lint forbids inline schema compiles.
const decodeAssistantCitation = Schema.decodeUnknownOption(AssistantCitation);

export const ComposerCitationExtension = Node.create({
  name: "composer-citation",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  /** Defines chip attributes and preserves structured data across HTML clipboard round trips. */
  addAttributes() {
    return {
      // HTML-only clipboards paste through the schema's HTML parsing, so the
      // object attribute must survive a string round trip; an undecodable
      // value degrades to null instead of handing the chip a string.
      citation: {
        default: null,
        /** Decodes pasted citation data, returning null for absent or malformed values. */
        parseHTML: (element) => {
          const value = element.getAttribute("citation");
          if (!value) return null;
          try {
            const decoded = decodeAssistantCitation(JSON.parse(value));
            return Option.getOrNull(decoded);
          } catch {
            return null;
          }
        },
        /** Serializes a present citation as JSON for HTML clipboard export. */
        renderHTML: (attributes) =>
          attributes.citation == null ? {} : { citation: JSON.stringify(attributes.citation) },
      },
      source: { default: "" },
      citeKey: { default: "" },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-composer-citation]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return ["span", { "data-composer-citation": "", ...HTMLAttributes }];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ComposerCitationNodeView);
  },
});

/** Comment panel host the composer renders below the prompt. */
export function ComposerCitationCommentProvider({
  value,
  editorState,
  children,
}: {
  readonly value: React.ComponentProps<typeof ComposerCitationCommentContext>["value"];
  readonly editorState: CitationEditorState;
  readonly children: ReactNode;
}) {
  return (
    <ComposerCitationEditorContext value={editorState}>
      <ComposerCitationCommentContext value={value}>{children}</ComposerCitationCommentContext>
    </ComposerCitationEditorContext>
  );
}

function findCitationPos(editor: Editor, citeKey: string): number | null {
  let found: number | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (found !== null) return false;
    if (node.type.name !== "composer-citation") return true;
    if ((node.attrs as Record<string, unknown>).citeKey === citeKey) found = pos;
    return true;
  });
  return found;
}

/** Rewrite a citation chip's comment in place; true when the chip was found. */
export function updateComposerCitationComment(
  editor: Editor,
  citeKey: string,
  comment: string,
): boolean {
  if (!editor.isEditable) return false;
  const pos = findCitationPos(editor, citeKey);
  if (pos === null) return false;
  const node = editor.state.doc.nodeAt(pos);
  if (!node || node.type.name !== "composer-citation") return false;
  const citation = node.attrs.citation as AssistantCitation | null;
  // A pasted chip with an undecodable citation has nothing to attach to.
  if (!citation) return false;
  const next = withAssistantCitationComment(citation, comment);
  if (next === citation) return true;
  const tr = editor.state.tr.setNodeMarkup(pos, undefined, {
    ...node.attrs,
    citation: next,
    source: serializeAssistantCitation(next),
  });
  editor.view.dispatch(tr);
  return true;
}

/** Delete a citation chip and return the caret to its left edge. */
export function removeComposerCitation(editor: Editor, citeKey: string): void {
  const pos = findCitationPos(editor, citeKey);
  if (pos === null) return;
  const node = editor.state.doc.nodeAt(pos);
  if (!node) return;
  editor
    .chain()
    .focus()
    .deleteRange({ from: pos, to: pos + node.nodeSize })
    .run();
}
